import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ConnectivityResponse, MapEdge } from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { useIsMobile } from "./hooks/useIsMobile.js";
import type { NodeTypeVisibility } from "./types.js";
import { MapControls } from "./ui/MapControls.js";
import { NetworkMap } from "./ui/NetworkMap.js";
import { ThalamosMark } from "./ui/ThalamosMark.js";

import "mapbox-gl/dist/mapbox-gl.css";

type TimeWindow = "7d" | "30d" | "90d" | "1y" | "all";
const VALID_TIME_WINDOWS = new Set<TimeWindow>([
  "7d",
  "30d",
  "90d",
  "1y",
  "all",
]);

const DEFAULT_VISIBILITY: NodeTypeVisibility = {
  "nhs-trust": false,
  "nhs-hospital": true,
  "local-authority": true,
  police: false,
  "place-of-safety": true,
  cqc: false,
  "cqc-provider": true,
  independent: false,
  prison: false,
  "incident-location": true,
  "combined-facility": true,
  "gp-practice": false,
  pcn: false,
};

const ALL_NODE_TYPES = Object.keys(DEFAULT_VISIBILITY) as Array<
  keyof NodeTypeVisibility
>;

/** Read initial state from URL search params */
function readUrlState() {
  const params = new URLSearchParams(window.location.search);

  const trust = params.get("trust") ?? null;

  const twParam = params.get("time") as TimeWindow | null;
  const timeWindow: TimeWindow =
    twParam && VALID_TIME_WINDOWS.has(twParam) ? twParam : "all";

  // Visibility: "show" param is a comma-separated list of visible types.
  // If absent, use defaults.
  const showParam = params.get("show");
  let visibility: NodeTypeVisibility;
  if (showParam !== null) {
    const enabledTypes = new Set(showParam.split(","));
    visibility = {} as NodeTypeVisibility;
    for (const t of ALL_NODE_TYPES) {
      visibility[t] = enabledTypes.has(t);
    }
  } else {
    visibility = { ...DEFAULT_VISIBILITY };
  }

  const lat = params.has("lat") ? Number(params.get("lat")) : null;
  const lng = params.has("lng") ? Number(params.get("lng")) : null;
  const zoom = params.has("zoom") ? Number(params.get("zoom")) : null;

  const mapPosition =
    lat != null &&
    lng != null &&
    zoom != null &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng) &&
    !Number.isNaN(zoom)
      ? { lat, lng, zoom }
      : null;

  return { trust, timeWindow, visibility, mapPosition };
}

/** Write all state to URL (replaceState — no history spam) */
function syncUrlState(
  trust: string | null,
  timeWindow: TimeWindow,
  visibility: NodeTypeVisibility,
  mapPosition: { lat: number; lng: number; zoom: number } | null,
) {
  const url = new URL(window.location.href);

  // Trust
  if (trust) url.searchParams.set("trust", trust);
  else url.searchParams.delete("trust");

  // Time window (omit default)
  if (timeWindow !== "all") url.searchParams.set("time", timeWindow);
  else url.searchParams.delete("time");

  // Visibility — store only when different from defaults
  const enabledTypes = ALL_NODE_TYPES.filter((t) => visibility[t]);
  const isDefault = ALL_NODE_TYPES.every(
    (t) => visibility[t] === DEFAULT_VISIBILITY[t],
  );
  if (!isDefault) url.searchParams.set("show", enabledTypes.join(","));
  else url.searchParams.delete("show");

  // Map position
  if (mapPosition) {
    url.searchParams.set("lat", mapPosition.lat.toFixed(4));
    url.searchParams.set("lng", mapPosition.lng.toFixed(4));
    url.searchParams.set("zoom", mapPosition.zoom.toFixed(2));
  } else {
    url.searchParams.delete("lat");
    url.searchParams.delete("lng");
    url.searchParams.delete("zoom");
  }

  window.history.replaceState(null, "", url.toString());
}

export function NetworkMapView() {
  const initialState = useRef(readUrlState());
  const [data, setData] = useState<ConnectivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(
    initialState.current.timeWindow,
  );
  const [visibility, setVisibility] = useState<NodeTypeVisibility>(
    initialState.current.visibility,
  );
  const [trustFilter, setTrustFilter] = useState<string | null>(
    initialState.current.trust,
  );
  const [controlsExpanded, setControlsExpanded] = useState(false);
  const isMobile = useIsMobile();
  const mapPositionRef = useRef<{
    lat: number;
    lng: number;
    zoom: number;
  } | null>(initialState.current.mapPosition);

  // URL sync: write all state whenever trust/time/visibility changes
  useEffect(() => {
    syncUrlState(trustFilter, timeWindow, visibility, mapPositionRef.current);
  }, [trustFilter, timeWindow, visibility]);

  const handleTrustFilterChange = useCallback((id: string | null) => {
    setTrustFilter(id);
  }, []);

  const handleTimeWindowChange = useCallback((tw: TimeWindow) => {
    setTimeWindow(tw);
  }, []);

  const handleVisibilityChange = useCallback((v: NodeTypeVisibility) => {
    setVisibility(v);
  }, []);

  const handleMapMove = useCallback(
    (lat: number, lng: number, zoom: number) => {
      mapPositionRef.current = { lat, lng, zoom };
      syncUrlState(trustFilter, timeWindow, visibility, mapPositionRef.current);
    },
    [trustFilter, timeWindow, visibility],
  );

  const loadData = useCallback(async (timeWindow: TimeWindow) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.connectivity.getConnectivity(
        timeWindow === "all" ? undefined : { window: timeWindow },
      );
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(timeWindow);
  }, [loadData, timeWindow]);

  // Trust filter: find owned nodes + edge-linked nodes
  const { filteredNodes, filteredEdges } = useMemo(() => {
    const allNodes = data?.nodes ?? [];
    const allEdges = data?.edges ?? [];

    // Apply type visibility first
    // Combined-facility nodes show when either hospital or POS is enabled
    const typeVisible = allNodes.filter((n) =>
      n.type === "combined-facility"
        ? visibility["nhs-hospital"] || visibility["place-of-safety"]
        : visibility[n.type],
    );

    if (!trustFilter) {
      // No trust filter — just use type visibility
      const ids = new Set(typeVisible.map((n) => n.id));
      return {
        filteredNodes: typeVisible,
        filteredEdges: allEdges.filter(
          (e) => ids.has(e.sourceId) && ids.has(e.targetId),
        ),
      };
    }

    // Owned = node IS the trust or has parentId pointing to it
    const ownedIds = new Set<string>();
    for (const n of allNodes) {
      if (n.id === trustFilter || n.parentId === trustFilter) {
        ownedIds.add(n.id);
      }
    }

    // Find edges touching owned nodes → collect linked node IDs
    const linkedIds = new Set<string>();
    const relevantEdges: MapEdge[] = [];
    for (const e of allEdges) {
      const srcOwned = ownedIds.has(e.sourceId);
      const tgtOwned = ownedIds.has(e.targetId);
      if (srcOwned || tgtOwned) {
        relevantEdges.push(e);
        if (srcOwned) linkedIds.add(e.targetId);
        if (tgtOwned) linkedIds.add(e.sourceId);
      }
    }

    // Combine owned + linked, then apply type visibility
    const allowedIds = new Set([...ownedIds, ...linkedIds]);
    const visibleFiltered = typeVisible.filter((n) => allowedIds.has(n.id));
    const visibleIds = new Set(visibleFiltered.map((n) => n.id));

    return {
      filteredNodes: visibleFiltered,
      filteredEdges: relevantEdges.filter(
        (e) => visibleIds.has(e.sourceId) && visibleIds.has(e.targetId),
      ),
    };
  }, [data, visibility, trustFilter]);

  const visibleNodes = filteredNodes;
  const visibleEdges = filteredEdges;

  // Compute geographic bounds for the selected trust's owned nodes
  const trustBounds = useMemo<
    [[number, number], [number, number]] | null
  >(() => {
    if (!trustFilter || !data) return null;
    const owned = data.nodes.filter(
      (n) => n.id === trustFilter || n.parentId === trustFilter,
    );
    if (owned.length === 0) return null;

    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    for (const n of owned) {
      if (n.lng < minLng) minLng = n.lng;
      if (n.lng > maxLng) maxLng = n.lng;
      if (n.lat < minLat) minLat = n.lat;
      if (n.lat > maxLat) maxLat = n.lat;
    }
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ];
  }, [trustFilter, data]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <NetworkMap
        nodes={visibleNodes}
        edges={visibleEdges}
        loading={loading}
        focusBounds={trustBounds}
        initialMapPosition={initialState.current.mapPosition}
        onMapMove={handleMapMove}
      />
      <MapControls
        timeWindow={timeWindow}
        onTimeWindowChange={handleTimeWindowChange}
        visibility={visibility}
        onVisibilityChange={handleVisibilityChange}
        nodeCount={visibleNodes.length}
        edgeCount={visibleEdges.length}
        allNodes={data?.nodes ?? []}
        trustFilter={trustFilter}
        onTrustFilterChange={handleTrustFilterChange}
        onExpandedChange={setControlsExpanded}
      />

      {/* Thalamos watermark + share button (hidden when mobile controls are open) */}
      {!(isMobile && controlsExpanded) && (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div
            style={{
              pointerEvents: "none",
              animation: "thalamos-glow 3s ease-in-out infinite",
            }}
          >
            <ThalamosMark size={28} opacity={0.55} />
          </div>
          <button
            onClick={() => {
              void navigator.clipboard.writeText(window.location.href);
            }}
            title="Copy link to clipboard"
            aria-label="Share"
            style={{
              background: "rgba(15, 23, 42, 0.7)",
              border: "1px solid rgba(148, 163, 184, 0.15)",
              borderRadius: 8,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#94a3b8",
              transition: "all 0.15s",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </button>
        </div>
      )}
      {error && (
        <div
          style={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(220, 38, 38, 0.9)",
            padding: "8px 16px",
            borderRadius: 8,
            fontSize: 14,
            zIndex: 100,
            color: "#fff",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
